import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from '../lib/api.js';
import { signInWithGoogle, signInWithEmailPassword, sendSignInLink, completeEmailLinkSignIn, signOut as firebaseSignOut, getCurrentUserEmail } from '../lib/firebase.js';
import { APP_DISPLAY_NAME } from '../lib/demo.js';
import { posHostName, siteBrand, isRetailProfile } from '../lib/site-config.js';

/** Retail-stall deployment — swap the pharmacy wording for shop wording. */
const RETAIL = isRetailProfile();

/**
 * Test-only email/password sign-in, enabled by the E2E build
 * (VITE_E2E_PASSWORD_LOGIN=1). Production builds never show it.
 */
const E2E_PASSWORD_LOGIN = import.meta.env.VITE_E2E_PASSWORD_LOGIN === '1';

/**
 * Unauthenticated landing page for pharmapos.com:
 *  - banner: "Welcome to PharmaPOS" (left) + hamburger menu (right) with
 *    Sign in with Google / Sign in with email link / separator / How-to,
 *    plus (when a Firebase session already exists) the account + Logout.
 *  - below: the Gateway intro deck auto-playing in a loop (iframe).
 *
 * Authenticated users never see this screen — main.ts restores the stored
 * session / persisted Firebase user and renders the app shell directly
 * (which carries the same hamburger menu with account + Logout).
 */
@customElement('landing-screen')
export class LandingScreen extends LitElement {
  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      min-height: 100dvh;
      background: #050d1f;
    }
    .banner {
      flex-shrink: 0; position: relative; z-index: 30;
      display: flex; align-items: center; justify-content: space-between;
      gap: 1rem; flex-wrap: wrap;
      padding: 0.6rem 1.2rem;
      color: #fff;
      background: linear-gradient(135deg, #081c47 0%, #0c3283 60%, #1668dd 100%);
      border-bottom: 1px solid rgba(255, 255, 255, 0.12);
    }
    .brand { display: flex; align-items: center; gap: 1rem; min-width: 0; }
    .brand img { width: 52px; height: 52px; border-radius: 12px; box-shadow: 0 10px 28px rgba(0, 0, 0, 0.4); }
    .brand h1 { font-size: 1.4rem; font-weight: 800; letter-spacing: -0.01em; margin: 0; }
    .brand p { margin: 0.2rem 0 0; font-size: 0.85rem; color: rgba(255, 255, 255, 0.75); }

    /* ── hamburger menu ── */
    .menu-wrap { display: flex; align-items: center; gap: 10px; position: relative; }
    .mini-sign { display: flex; align-items: center; gap: 7px; font-size: 0.82rem; color: rgba(255, 255, 255, 0.85); }
    .menu-btn {
      display: flex; align-items: center; justify-content: center;
      width: 44px; height: 44px; border-radius: 11px;
      border: 1px solid rgba(255, 255, 255, 0.3);
      background: rgba(255, 255, 255, 0.14); color: #fff;
      cursor: pointer; transition: background 0.15s;
    }
    .menu-btn:hover { background: rgba(255, 255, 255, 0.26); }
    .menu-btn svg { width: 22px; height: 22px; }
    .menu-backdrop { position: fixed; inset: 0; z-index: 70; }
    .menu {
      position: absolute; top: calc(100% + 10px); right: 0; width: 282px;
      background: linear-gradient(160deg, #0a2352, #0d3a8f);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 14px;
      box-shadow: 0 30px 70px rgba(0, 0, 0, 0.55);
      padding: 8px; z-index: 80;
    }
    .menu-item {
      display: flex; align-items: center; gap: 11px; width: 100%;
      padding: 11px 12px; border: none; border-radius: 9px;
      background: transparent; color: #fff;
      font-size: 0.9rem; font-weight: 600; font-family: inherit;
      text-align: left; cursor: pointer; text-decoration: none;
    }
    .menu-item:hover { background: rgba(255, 255, 255, 0.12); }
    .menu-item .mi {
      flex: 0 0 20px; width: 20px; height: 20px;
      display: flex; align-items: center; justify-content: center; font-size: 16px;
    }
    .menu-item .mi svg { width: 17px; height: 17px; }
    .menu-sep { border: none; border-top: 1px solid rgba(255, 255, 255, 0.16); margin: 6px 6px; }
    .menu-user { display: flex; align-items: center; gap: 10px; padding: 6px 12px 8px; }
    .menu-user .mu-ico {
      flex: 0 0 30px; width: 30px; height: 30px; border-radius: 50%;
      background: rgba(255, 255, 255, 0.14);
      display: flex; align-items: center; justify-content: center; font-size: 15px;
    }
    .menu-user .mu-email {
      min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      font-size: 0.84rem; color: rgba(255, 255, 255, 0.9);
    }
    .menu-head { display: flex; align-items: center; gap: 9px; padding: 4px 4px 8px; }
    .menu-back {
      display: flex; align-items: center; justify-content: center;
      width: 30px; height: 30px; border: none; border-radius: 8px;
      background: rgba(255, 255, 255, 0.12); color: #fff;
      cursor: pointer; font-size: 1rem;
    }
    .menu-back:hover { background: rgba(255, 255, 255, 0.24); }
    .menu-title { font-size: 0.88rem; font-weight: 700; }
    .email-form { position: relative; display: flex; padding: 2px 6px 8px; }
    .email-form input {
      width: 100%;
      padding: 9px 40px 9px 11px;
      border-radius: 8px; font-size: 0.86rem; font-family: inherit;
      border: 1px solid rgba(255, 255, 255, 0.35);
      background: rgba(255, 255, 255, 0.1);
      color: #fff;
    }
    input::placeholder { color: rgba(255, 255, 255, 0.55); }
    input:focus { outline: none; border-color: #8ec5ff; }
    .btn-inside {
      position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
      width: 30px; height: 26px;
      display: flex; align-items: center; justify-content: center;
      border: none; border-radius: 6px; cursor: pointer;
      background: #1a73e8; color: #fff;
    }
    .btn-inside:hover:not(:disabled) { background: #1557b0; }
    .btn-inside:disabled { opacity: 0.6; cursor: not-allowed; }
    .btn-inside svg { width: 15px; height: 15px; }
    .btn-inside .spinner { width: 13px; height: 13px; border-width: 2px; }
    .msg { font-size: 0.76rem; padding: 2px 12px 6px; }
    .msg.error { color: #ffb4b4; }
    .signing {
      display: flex; align-items: center; justify-content: center; gap: 0.6rem;
      color: rgba(255, 255, 255, 0.85); font-size: 0.88rem; padding: 1.1rem 0.6rem;
    }
    .spinner {
      width: 16px; height: 16px; border: 2px solid rgba(255, 255, 255, 0.35);
      border-top-color: #fff; border-radius: 50%;
      animation: spin 0.8s linear infinite; flex-shrink: 0;
    }
    @keyframes spin { to { transform: rotate(360deg); } }
    .spinner.dark { border-color: rgba(14, 32, 56, 0.3); border-top-color: #0e2038; width: 20px; height: 20px; }

    /* ── page content (mobile first: single column) ── */
    main { flex: 1; color: #fff; }
    .wrap { width: 100%; max-width: 1080px; margin: 0 auto; padding: 0 1.1rem; }
    .hero {
      padding: 2.2rem 0 2rem; text-align: center;
      background: radial-gradient(120% 80% at 50% 0%, #0f3b94 0%, #050d1f 70%);
    }
    .hero h2 {
      margin: 0 0 0.8rem; font-weight: 800; letter-spacing: -0.02em;
      font-size: clamp(1.8rem, 7vw, 3.2rem); line-height: 1.12;
    }
    .hero h2 em { font-style: normal; color: #8ec5ff; }
    .hero p.lead {
      margin: 0 auto 1.5rem; max-width: 34rem;
      font-size: clamp(1rem, 3.6vw, 1.2rem); line-height: 1.55; color: rgba(255, 255, 255, 0.8);
    }
    .cta-row { display: flex; flex-direction: column; gap: 0.7rem; max-width: 22rem; margin: 0 auto; }
    .btn {
      display: flex; align-items: center; justify-content: center; gap: 0.6rem;
      min-height: 52px; padding: 0 1.2rem; border-radius: 14px;
      font-family: inherit; font-size: 1rem; font-weight: 700; cursor: pointer;
      border: 1px solid transparent; text-decoration: none;
    }
    .btn svg { width: 20px; height: 20px; }
    .btn-primary { background: #fff; color: #0e2038; box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35); }
    .btn-primary:hover { background: #eef4ff; }
    .btn-ghost { background: rgba(255, 255, 255, 0.1); color: #fff; border-color: rgba(255, 255, 255, 0.3); }
    .btn-ghost:hover { background: rgba(255, 255, 255, 0.18); }
    .btn:disabled { opacity: 0.6; cursor: not-allowed; }
    .link-btn {
      background: none; border: none; color: #8ec5ff; font: inherit; font-size: 0.92rem;
      text-decoration: underline; cursor: pointer; padding: 0.5rem;
    }
    .hero .err { margin-top: 0.8rem; color: #ffb4b4; font-size: 0.88rem; }

    section.block { padding: 2rem 0; }
    section.block h3 {
      margin: 0 0 1.1rem; text-align: center; font-size: clamp(1.3rem, 5vw, 1.9rem); font-weight: 800;
    }
    .cards { display: grid; grid-template-columns: 1fr; gap: 0.8rem; }
    .card {
      background: rgba(255, 255, 255, 0.06); border: 1px solid rgba(255, 255, 255, 0.12);
      border-radius: 16px; padding: 1.1rem 1.2rem;
    }
    .card .ci {
      width: 44px; height: 44px; border-radius: 12px; margin-bottom: 0.7rem;
      display: flex; align-items: center; justify-content: center; font-size: 22px;
      background: linear-gradient(135deg, #1668dd, #0c3283);
    }
    .card h4 { margin: 0 0 0.3rem; font-size: 1.05rem; }
    .card p { margin: 0; font-size: 0.92rem; line-height: 1.5; color: rgba(255, 255, 255, 0.72); }
    .steps { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.8rem; counter-reset: s; }
    .steps li {
      counter-increment: s; position: relative;
      padding: 1rem 1rem 1rem 4rem; border-radius: 16px;
      background: rgba(255, 255, 255, 0.06); border: 1px solid rgba(255, 255, 255, 0.12);
      font-size: 0.95rem; line-height: 1.5; color: rgba(255, 255, 255, 0.8);
    }
    .steps li::before {
      content: counter(s); position: absolute; left: 1rem; top: 50%; transform: translateY(-50%);
      width: 2.2rem; height: 2.2rem; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      font-weight: 800; background: #1a73e8; color: #fff;
    }
    .steps li b { display: block; color: #fff; font-size: 1rem; margin-bottom: 0.1rem; }
    .intro-cta { text-align: center; padding-bottom: 2rem; }
    footer.foot {
      padding: 1.4rem 1rem calc(6rem + env(safe-area-inset-bottom));
      text-align: center; font-size: 0.8rem; color: rgba(255, 255, 255, 0.5);
    }

    /* sticky sign-in bar (phones only) */
    .sticky-bar {
      position: fixed; left: 0; right: 0; bottom: 0; z-index: 20;
      padding: 0.7rem 1rem calc(0.7rem + env(safe-area-inset-bottom));
      background: rgba(5, 13, 31, 0.92); backdrop-filter: blur(8px);
      border-top: 1px solid rgba(255, 255, 255, 0.14);
    }
    .sticky-bar .btn { width: 100%; }

    /* intro deck modal */
    .deck-modal { position: fixed; inset: 0; z-index: 110; background: rgba(3, 10, 26, 0.9); display: flex; flex-direction: column; }
    .deck-modal .bar { display: flex; justify-content: flex-end; padding: 0.6rem; }
    .deck-modal .close {
      width: 44px; height: 44px; border-radius: 12px; border: 1px solid rgba(255, 255, 255, 0.3);
      background: rgba(255, 255, 255, 0.12); color: #fff; font-size: 1.2rem; cursor: pointer;
    }
    .deck-modal iframe { flex: 1; width: 100%; border: 0; background: #050d1f; }

    @media (min-width: 768px) {
      .hero { padding: 4.5rem 0 4rem; }
      .cta-row { flex-direction: row; max-width: none; justify-content: center; }
      .btn { min-width: 15rem; }
      .cards { grid-template-columns: repeat(2, 1fr); gap: 1rem; }
      .steps { grid-template-columns: repeat(3, 1fr); }
      .steps li { padding: 4.2rem 1.2rem 1.2rem; text-align: center; }
      .steps li::before { left: 50%; top: 1.1rem; transform: translateX(-50%); }
      .sticky-bar { display: none; }
      footer.foot { padding-bottom: 1.6rem; }
    }
    @media (min-width: 1024px) {
      .cards { grid-template-columns: repeat(4, 1fr); }
    }
    @media (prefers-reduced-motion: reduce) {
      * { animation-duration: 0.01ms !important; transition: none !important; }
    }

    /* ── "check your email" popup ── */
    .overlay {
      position: fixed; inset: 0; z-index: 100;
      background: rgba(3, 10, 26, 0.68);
      display: flex; align-items: center; justify-content: center;
      backdrop-filter: blur(3px);
    }
    .popup {
      background: #fff; color: #0e2038;
      border-radius: 16px;
      padding: 30px 32px 26px;
      width: min(460px, 90vw);
      text-align: center;
      box-shadow: 0 30px 80px rgba(0, 0, 0, 0.55);
    }
    .popup .popup-ico { font-size: 46px; margin-bottom: 12px; }
    .popup h3 { margin: 0 0 10px; font-size: 1.2rem; font-weight: 800; }
    .popup p { margin: 0 0 20px; font-size: 0.92rem; line-height: 1.55; color: #4a5d7a; }
    .popup p b { color: #0e2038; }
    .btn-popup {
      background: #1a73e8; color: #fff; border: none; border-radius: 9px;
      padding: 11px 40px; font-size: 0.95rem; font-weight: 700;
      font-family: inherit; cursor: pointer;
    }
    .btn-popup:hover { background: #1557b0; }

    @media (max-width: 640px) {
      .brand p { display: none; }
      .brand h1 { font-size: 1.2rem; }
      .brand img { width: 40px; height: 40px; }
      .banner { flex-wrap: nowrap; padding-top: max(0.6rem, env(safe-area-inset-top)); }
      /* thumb-reachable bottom sheet instead of a top-right dropdown */
      .menu {
        position: fixed; top: auto; left: 0; right: 0; bottom: 0; width: auto;
        border-radius: 18px 18px 0 0;
        padding: 10px 10px calc(14px + env(safe-area-inset-bottom));
      }
      .menu-backdrop { background: rgba(3, 10, 26, 0.55); }
      .menu-item { min-height: 50px; font-size: 1rem; }
      .email-form input { min-height: 48px; font-size: 16px; }
      .btn-inside { height: 36px; width: 40px; }
    }
  `;

  @state() private _loading = false;   // Google sign-in / email-link completion in progress
  @state() private _sending = false;   // sending the sign-in-link email
  @state() private _error = '';
  @state() private _email = '';
  @state() private _sentPopup = '';    // email the link was sent to ('' = popup hidden)
  @state() private _menuOpen = false;
  @state() private _menuView: 'menu' | 'email' | 'password' = 'menu';
  @state() private _passwordEmail = '';
  @state() private _password = '';
  @state() private _signedInAs = '';   // existing Firebase session (email) — offers Logout
  @state() private _introOpen = false; // intro deck modal

  async connectedCallback() {
    super.connectedCallback();
    // Check for email link sign-in completion
    try {
      const idToken = await completeEmailLinkSignIn();
      if (idToken) {
        // Strip the one-time link params so a reload doesn't re-process them.
        window.history.replaceState({}, '', '/');
        await this._loginWithToken(idToken);
      }
    } catch {
      // Not an email link URL, or sign-in failed
    }
    // A Firebase session can outlive a failed gateway login — the menu then
    // offers Logout so the user can switch accounts.
    this._refreshSignedIn();
  }

  private _refreshSignedIn() {
    this._signedInAs = getCurrentUserEmail() || '';
  }

  private async _loginWithToken(idToken: string) {
    this._loading = true;
    this._error = '';
    try {
      const result = await api.login(idToken);
      if (result.success) {
        api.setSessionKey(result.data.sessionKey);
        this.dispatchEvent(
          new CustomEvent('login-success', {
            detail: {
              sessionKey: result.data.sessionKey,
              email: result.data.email,
              ownedDBs: result.data.ownedDBs,
            },
            bubbles: true,
            composed: true,
          }),
        );
      }
    } catch (err: any) {
      this._error = err.message || 'Login failed';
      await firebaseSignOut();
      this._refreshSignedIn();
    } finally {
      this._loading = false;
    }
  }

  private async _handleGoogleSignIn() {
    if (this._loading || this._sending) return;
    this._loading = true;
    this._error = '';
    try {
      const idToken = await signInWithGoogle();
      await this._loginWithToken(idToken);
    } catch (err: any) {
      if (err.code !== 'auth/popup-closed-by-user') {
        this._error = err.message || 'Google sign-in failed';
      }
      this._loading = false;
      this._refreshSignedIn();
    }
  }

  private async _handleSendLink() {
    if (this._sending || this._loading) return;
    const email = this._email.trim();
    if (!email) {
      this._error = 'Please enter your email address.';
      return;
    }
    this._sending = true;
    this._error = '';
    try {
      await sendSignInLink(email);
      this._sentPopup = email;
      this._closeMenu();
    } catch (err: any) {
      this._error = err.message || 'Failed to send sign-in link';
    } finally {
      this._sending = false;
    }
  }

  /**
   * E2E-only password sign-in. Renders only when the bundle was built with
   * VITE_E2E_PASSWORD_LOGIN=1 so production keeps Google/email-link only.
   */
  private async _handlePasswordSignIn() {
    if (this._loading || this._sending) return;
    const email = this._passwordEmail.trim();
    if (!email || !this._password) {
      this._error = 'Enter the test email and password.';
      return;
    }
    this._loading = true;
    this._error = '';
    try {
      const idToken = await signInWithEmailPassword(email, this._password);
      await this._loginWithToken(idToken);
    } catch (err: any) {
      this._error = err.message || 'Password sign-in failed';
      this._loading = false;
      this._refreshSignedIn();
    }
  }

  private async _handleLogout() {
    try { await firebaseSignOut(); } catch { /* ignore */ }
    this._refreshSignedIn();
    this._closeMenu();
  }

  private _toggleMenu() {
    this._menuOpen = !this._menuOpen;
    this._menuView = 'menu';
    this._error = '';
  }

  private _openEmailSignIn() {
    this._menuOpen = true;
    this._menuView = 'email';
    this._error = '';
  }

  private _closeMenu() {
    this._menuOpen = false;
    this._menuView = 'menu';
    this._error = '';
  }

  private _closePopup() {
    this._sentPopup = '';
  }

  private _googleIcon() {
    return html`
      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
      </svg>
    `;
  }

  private _renderMenu() {
    if (this._loading) {
      return html`
        <div class="menu">
          <div class="signing"><span class="spinner"></span><span>Signing in&hellip;</span></div>
        </div>`;
    }
    if (this._menuView === 'email') {
      return html`
        <div class="menu" data-testid="landing-menu">
          <div class="menu-head">
            <button class="menu-back" title="Back" aria-label="Back" @click=${() => { this._menuView = 'menu'; this._error = ''; }}>←</button>
            <span class="menu-title">Sign in with email link</span>
          </div>
          <div class="email-form">
            <input
              type="email"
              data-testid="login-email-input"
              placeholder="you@example.com"
              .value=${this._email}
              ?disabled=${this._sending}
              @input=${(e: InputEvent) => this._email = (e.target as HTMLInputElement).value}
              @keydown=${(e: KeyboardEvent) => e.key === 'Enter' && this._handleSendLink()}
            />
            <button
              class="btn-inside"
              title="Send sign-in link"
              aria-label="Send sign-in link"
              data-testid="login-email-submit"
              @click=${this._handleSendLink}
              ?disabled=${this._sending}
            >
              ${this._sending ? html`<span class="spinner"></span>` : html`
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                  <polyline points="10 17 15 12 10 7"/>
                  <line x1="15" y1="12" x2="3" y2="12"/>
                </svg>`}
            </button>
          </div>
          ${this._error ? html`<div class="msg error" data-testid="login-error">${this._error}</div>` : ''}
        </div>`;
    }
    if (this._menuView === 'password') {
      return html`
        <div class="menu" data-testid="landing-menu">
          <div class="menu-head">
            <button class="menu-back" title="Back" aria-label="Back" @click=${() => { this._menuView = 'menu'; this._error = ''; }}>←</button>
            <span class="menu-title">E2E password sign-in</span>
          </div>
          <div class="email-form">
            <input
              type="email"
              data-testid="login-password-email"
              placeholder="test@example.com"
              .value=${this._passwordEmail}
              ?disabled=${this._loading}
              @input=${(e: InputEvent) => this._passwordEmail = (e.target as HTMLInputElement).value}
            />
          </div>
          <div class="email-form">
            <input
              type="password"
              data-testid="login-password-input"
              placeholder="Password"
              .value=${this._password}
              ?disabled=${this._loading}
              @input=${(e: InputEvent) => this._password = (e.target as HTMLInputElement).value}
              @keydown=${(e: KeyboardEvent) => e.key === 'Enter' && this._handlePasswordSignIn()}
            />
            <button
              class="btn-inside"
              title="Sign in"
              aria-label="Sign in"
              data-testid="login-password-submit"
              @click=${this._handlePasswordSignIn}
              ?disabled=${this._loading}
            >
              ${this._loading ? html`<span class="spinner"></span>` : html`
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M5 12h14"/>
                  <polyline points="12 5 19 12 12 19"/>
                </svg>`}
            </button>
          </div>
          ${this._error ? html`<div class="msg error" data-testid="login-error">${this._error}</div>` : ''}
        </div>`;
    }
    return html`
      <div class="menu" data-testid="landing-menu">
        <button class="menu-item" data-testid="login-google" @click=${this._handleGoogleSignIn}>
          <span class="mi">${this._googleIcon()}</span>
          Sign in with Google
        </button>
        <button class="menu-item" data-testid="login-email-link" @click=${() => { this._menuView = 'email'; this._error = ''; }}>
          <span class="mi">✉️</span>
          Sign in with email link
        </button>
        ${E2E_PASSWORD_LOGIN ? html`
          <button class="menu-item" data-testid="login-password" @click=${() => { this._menuView = 'password'; this._error = ''; }}>
            <span class="mi">🔑</span>
            Sign in with password (E2E)
          </button>` : ''}
        <hr class="menu-sep">
        <a class="menu-item" href="/marketing/gateway-connect.html" target="_blank" rel="noopener">
          <span class="mi">🚀</span>
          How to connect your store
        </a>
        ${this._signedInAs ? html`
          <hr class="menu-sep">
          <div class="menu-user">
            <span class="mu-ico">👤</span>
            <span class="mu-email" title=${this._signedInAs}>${this._signedInAs}</span>
          </div>
          <button class="menu-item" @click=${this._handleLogout}>
            <span class="mi">🚪</span>
            Logout
          </button>` : ''}
        ${this._error ? html`<div class="msg error">${this._error}</div>` : ''}
      </div>`;
  }

  render() {
    return html`
      <header class="banner">
        <div class="brand">
          <img src="/icons/app-icon.svg" alt="" />
          <div>
            <h1>Welcome to ${APP_DISPLAY_NAME}</h1>
            <p>Sign in to access your ${RETAIL ? 'shop' : 'pharmacy'} management system</p>
          </div>
        </div>
        <div class="menu-wrap">
          ${this._loading && !this._menuOpen
            ? html`<span class="mini-sign"><span class="spinner"></span>Signing in&hellip;</span>`
            : ''}
          <button class="menu-btn" title="Menu" aria-label="Menu" data-testid="menu-toggle" @click=${this._toggleMenu}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round">
              <line x1="4" y1="7" x2="20" y2="7"/>
              <line x1="4" y1="12" x2="20" y2="12"/>
              <line x1="4" y1="17" x2="20" y2="17"/>
            </svg>
          </button>
          ${this._menuOpen ? html`
            <div class="menu-backdrop" @click=${this._closeMenu}></div>
            ${this._renderMenu()}
          ` : ''}
        </div>
      </header>
      <main>
        <section class="hero">
          <div class="wrap">
            <h2>Run your shop <em>from anywhere</em></h2>
            <p class="lead">Check stock, track sales and manage every outlet from your phone — securely connected to your ${siteBrand()} store.</p>
            <div class="cta-row">
              <button class="btn btn-primary" ?disabled=${this._loading} @click=${this._handleGoogleSignIn}>
                ${this._loading ? html`<span class="spinner dark"></span>` : this._googleIcon()}
                ${this._loading ? 'Signing in…' : 'Sign in with Google'}
              </button>
              <button class="btn btn-ghost" @click=${this._openEmailSignIn}>✉️ Sign in with email link</button>
            </div>
            ${this._error && !this._menuOpen ? html`<div class="err">${this._error}</div>` : ''}
          </div>
        </section>

        <section class="block">
          <div class="wrap">
            <h3>Everything your shop needs</h3>
            <div class="cards">
              <div class="card"><div class="ci">🔎</div><h4>Stock search</h4><p>Find any item and its quantity across outlets in seconds.</p></div>
              <div class="card"><div class="ci">📊</div><h4>Sales reports</h4><p>See daily takings and trends the moment they happen.</p></div>
              <div class="card"><div class="ci">🏬</div><h4>Multi-outlet</h4><p>Switch between stores with one tap, with per-person access roles.</p></div>
              <div class="card"><div class="ci">🛒</div><h4>Online mall</h4><p>Publish products and receive orders from customers.</p></div>
            </div>
          </div>
        </section>

        <section class="block">
          <div class="wrap">
            <h3>Get connected in 3 steps</h3>
            <ol class="steps">
              <li><b>Connect your store</b>Link your ${siteBrand()} server to ${posHostName()}.</li>
              <li><b>Add your people</b>Give each person an email and the roles they need.</li>
              <li><b>Sign in</b>Use Google or an email link — no passwords to remember.</li>
            </ol>
          </div>
        </section>

        <section class="block intro-cta">
          <div class="wrap cta-row">
            <button class="btn btn-ghost" @click=${() => { this._introOpen = true; }}>▶ Watch intro</button>
            <a class="btn btn-ghost" href="/marketing/gateway-connect.html" target="_blank" rel="noopener">🚀 How to connect your store</a>
          </div>
        </section>

        <footer class="foot">&copy; ${new Date().getFullYear()} ${APP_DISPLAY_NAME}</footer>
      </main>

      <div class="sticky-bar">
        <button class="btn btn-primary" ?disabled=${this._loading} @click=${this._handleGoogleSignIn}>
          ${this._googleIcon()} Sign in with Google
        </button>
      </div>

      ${this._introOpen ? html`
        <div class="deck-modal" role="dialog" aria-modal="true" aria-label="Introduction">
          <div class="bar"><button class="close" aria-label="Close" @click=${() => { this._introOpen = false; }}>✕</button></div>
          <iframe
            src="/marketing/gateway-intro.html?play=1&loop=1&chrome=0"
            title="${siteBrand()} Gateway — introduction"
          ></iframe>
        </div>` : ''}
      ${this._sentPopup ? html`
        <div class="overlay" @click=${(e: Event) => { if (e.target === e.currentTarget) this._closePopup(); }}>
          <div class="popup" role="dialog" aria-modal="true">
            <div class="popup-ico">📧</div>
            <h3>Check your email</h3>
            <p>A sign-in link has been sent to <b>${this._sentPopup}</b>.<br>
               Open the email and click the link to continue signing in.</p>
            <button class="btn-popup" @click=${this._closePopup}>OK</button>
          </div>
        </div>` : ''}
    `;
  }
}
